import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Autocomplete,
  Option,
} from '@beeline/design-system-react/extracted-styles';

import { Floor } from 'src/api/types';
import {
  BuildingSelect,
  BuildingSelectItem,
  toBuildingSelectItem,
} from 'src/components/BuildingSelect/BuildingSelect';
import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import {
  toTownSelectItem,
  TownSelect,
  TownSelectItem,
} from 'src/components/TownSelect/TownSelect';
import { getDeviceSize } from 'src/store/agent/selectors';
import { getDesksState, getFilteredDesks } from 'src/store/desks/selectors';
import { desksActions, openDesk } from 'src/store/desks/slice';
import { fetchFloors } from 'src/store/floors/services';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { DeviceSize, PlaceType } from 'src/types';

import { DeskBookingFilterButton } from '../../DeskBookingFilters/DeskBookingFilterButton';
import { DeskBookingSettings } from '../../DeskBookingSettings/DeskBookingSettings';
import { DeskOfficeSidesheetButton } from '../../DeskOffice/DeskOfficeSidesheetButton';

import classes from './DesktopPageActions.module.scss';

const renderValue = (option: Option<string>): string => option.value ?? '';

export const DesktopPageActions: FC = () => {
  const deviceSize = useSelector(getDeviceSize);
  const dispatch = useAppDispatch();
  const { town, building } = useSelector(getDesksState);

  const handleTownChange = useCallback(
    (value: TownSelectItem | undefined) => {
      dispatch(sidesheetActions.close());
      dispatch(desksActions.setTown(value?.value));
      dispatch(desksActions.setBuilding(undefined));
      dispatch(desksActions.setFloor(undefined));
    },
    [dispatch],
  );

  const handleBuildingChange = useCallback(
    async (value: BuildingSelectItem | undefined) => {
      let floor: Floor | undefined;
      dispatch(sidesheetActions.close());
      dispatch(desksActions.setBuilding(value?.value));

      if (value?.value) {
        const floors = await dispatch(
          fetchFloors({
            cached: true,
            buildingId: value.value.buildingId,
          }),
        ).unwrap();
        floor = floors.find(f => !f.isParking);
      }

      await dispatch(desksActions.setFloor(floor));
    },
    [dispatch],
  );

  const [deskSearchValue, setDeskSearchValue] = useState<Option<string> | null>(
    null,
  );
  const [deskSearchInput, setDeskSearchInput] = useState('');
  const filteredBookingObjects = useSelector(getFilteredDesks);
  const { scrollTo } = useFloorMap();

  const deskSearchOptions = useMemo((): Option<string>[] => {
    const result: Option<string>[] = [];

    for (const [key, value] of filteredBookingObjects) {
      if (!key || !value.title) continue;

      result.push({ id: key, value: value.title });
    }

    return result.sort((a, b) => a.value.localeCompare(b.value));
  }, [filteredBookingObjects]);

  const filteredDeskSearchOptions = useMemo((): Option<string>[] => {
    const search = deskSearchInput.toLowerCase();

    return deskSearchOptions.filter(option =>
      option.value.toLowerCase().includes(search),
    );
  }, [deskSearchInput, deskSearchOptions]);

  const handleDeskSearchChange = useCallback(
    async (option: Option<string>) => {
      setDeskSearchValue(option);

      const found = filteredBookingObjects.get(option.id as string);
      if (found && found.type === PlaceType.Desk) {
        dispatch(openDesk(found.mapPlaceId));

        scrollTo(found);
      }
    },
    [dispatch, filteredBookingObjects, scrollTo],
  );

  const handleDeskSearchClear = useCallback(() => {
    setDeskSearchValue(null);
  }, []);

  const townSelectItem = useMemo(() => toTownSelectItem(town), [town]);
  const buildingSelectItem = useMemo(
    () => toBuildingSelectItem(building),
    [building],
  );

  return (
    <div className={classes.actions}>
      {deviceSize === DeviceSize.S ? (
        <DeskOfficeSidesheetButton small />
      ) : (
        <div className={classes.main}>
          <div className={classes.town}>
            <TownSelect town={townSelectItem} onChange={handleTownChange} />
          </div>
          <div className={classes.building}>
            <BuildingSelect
              building={buildingSelectItem}
              onChange={handleBuildingChange}
              townId={town?.townId}
              disabled={!town}
              key={town?.townId}
            />
          </div>
        </div>
      )}
      <div className={classes.additional}>
        <div className={classes.date}>
          <DeskBookingSettings />
        </div>
        <div className={classes.deskSearch}>
          <Autocomplete
            placeholder='Место'
            type='search'
            size='medium'
            disabled={!deskSearchOptions.length}
            options={filteredDeskSearchOptions}
            value={deskSearchValue}
            noOptionsText='не найдено'
            onChange={handleDeskSearchChange}
            renderValue={renderValue}
            onInputChange={setDeskSearchInput}
            onInputClear={handleDeskSearchClear}
            loading={!deskSearchOptions.length}
            fullWidth
          />
        </div>
        <div className={classes.buttons}>
          <DeskBookingFilterButton />
        </div>
      </div>
    </div>
  );
};
